import { ChangeEvent, useCallback } from 'react'
import { BillTo } from '@user/store/entityLocations'

type UseHandlersProps = {
  allBillTos: BillTo[]
  loading: boolean
  isFetchingMore: boolean
  page: number
  pages: number
  searchText: string
  onChange: (locationId: string, locationCode?: string) => void
  onClearClick: () => void
  onChangePage: (event: unknown, page: number) => void
  onChangeSearchText: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  onClearSearchText: () => void
  setSelectedBillToId: (id: string | undefined) => void
  setIsFetchingMore: (isFetching: boolean) => void
  setOpen: (open: boolean) => void
}

export const useHandlers = (props: UseHandlersProps) => {
  const {
    allBillTos,
    loading,
    isFetchingMore,
    page,
    pages,
    searchText,
    onChange,
    onClearClick,
    onChangePage,
    onChangeSearchText,
    onClearSearchText,
    setSelectedBillToId,
    setIsFetchingMore,
    setOpen,
  } = props

  const handleChangeRadio = useCallback(
    (billToId: string) => {
      const selected = allBillTos.find((x) => x.id === billToId)
      setSelectedBillToId(billToId)
      onChange(billToId, selected?.number)
      setOpen(false)
    },
    [onChange, allBillTos, setSelectedBillToId, setOpen],
  )

  const handleChangeSearchText = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeSearchText(e)
      const { value } = e.target
      if (value === '') {
        onClearClick()
        setSelectedBillToId(undefined)
        setOpen(false)
      } else {
        setOpen(true)
      }
    },
    [onChangeSearchText, onClearClick, setSelectedBillToId, setOpen],
  )

  const handleClearSearchText = useCallback(() => {
    onClearSearchText()
    onClearClick()
    setSelectedBillToId(undefined)
    setOpen(false)
  }, [onClearSearchText, onClearClick, setSelectedBillToId, setOpen])

  const handleScrollSafe = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
      if (loading || isFetchingMore || page >= pages) return

      if (scrollHeight - scrollTop - clientHeight < 50) {
        setIsFetchingMore(true)
        onChangePage(null, page + 1)
      }
    },
    [loading, isFetchingMore, page, pages, onChangePage, setIsFetchingMore],
  )

  const handleClickAway = () => {
    setOpen(false)
  }

  const handleInputFocus = () => {
    if (searchText && allBillTos.length > 0) {
      setOpen(true)
    }
  }

  return {
    handleChangeRadio,
    handleChangeSearchText,
    handleClearSearchText,
    handleScrollSafe,
    handleClickAway,
    handleInputFocus,
  }
}
